import dayjs from "dayjs";

import { useMemo, useRef } from "react";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { IconButton } from "@mui/material";
import { BarChart } from "@mui/x-charts";

import { ApiRoutesItem, ApiRoutesItemPoints } from "@/api/routes";
import { CabinsColors } from "@/constants/config";
import useMore from "@/hooks/useMore";

export default function RouteChart(props: {
  data: ApiRoutesItem[];
  onItemClick: (d: string) => void;
}) {
  const { data } = props;

  const chartRef = useRef();

  const dates = useMemo(() => {
    const today = dayjs();

    const futureDates = [];

    for (let i = 0; i < 365; i++) {
      const nextDate = today.add(i, "day").format("YYYY-MM-DD");
      futureDates.push(nextDate);
    }

    return futureDates;
  }, []);

  const { currentPageData, hasMore, pageIndex, handleNext, handlePrev } = useMore(dates, {
    pageSize: 80,
  });

  const currentPageSourceData = useMemo(() => {
    return currentPageData.map((date) => {
      const item = data?.find((item) => item?.date === date);
      return item;
    });
  }, [currentPageData, data]);

  const economyData = useMemo(() => {
    return currentPageSourceData.map((item) => {
      const points = item?.points || ({} as ApiRoutesItemPoints);
      return points.y;
    });
  }, [currentPageSourceData]);

  const premiumEconomyData = useMemo(() => {
    return currentPageSourceData.map((item) => {
      const points = item?.points || ({} as ApiRoutesItemPoints);
      return points.w;
    });
  }, [currentPageSourceData]);

  const businessData = useMemo(() => {
    return currentPageSourceData.map((item) => {
      const points = item?.points || ({} as ApiRoutesItemPoints);
      return points.j;
    });
  }, [currentPageSourceData]);

  const firstData = useMemo(() => {
    return currentPageSourceData.map((item) => {
      const points = item?.points || ({} as ApiRoutesItemPoints);
      return points.f;
    });
  }, [currentPageSourceData]);

  const valueFormatter = (value: number | null) => {
    const d = value?.toLocaleString();

    if (d) {
      return `${d} pts`;
    }

    return "Not Available";
  };

  return (
    <div className="px-[20px] pb-[20px] relative">
      <div className="absolute top-[50%] left-[0] translate-y-[-50%] z-10">
        <IconButton
          aria-label="delete"
          size="medium"
          disabled={pageIndex === 1}
          onClick={() => handlePrev()}
        >
          <KeyboardArrowLeftIcon fontSize="inherit" />
        </IconButton>
      </div>

      <div className="absolute top-[50%] right-[0] translate-y-[-50%] z-10">
        <IconButton
          aria-label="delete"
          size="medium"
          disabled={!hasMore}
          onClick={() => handleNext()}
        >
          <KeyboardArrowRightIcon fontSize="inherit" />
        </IconButton>
      </div>

      <BarChart
        ref={chartRef}
        borderRadius={2}
        height={300}
        grid={{
          vertical: false,
          horizontal: true,
        }}
        leftAxis={null}
        xAxis={[
          {
            scaleType: "band",
            data: currentPageData,
            valueFormatter: (date, context) => {
              const target = currentPageSourceData.find((item) => item?.date === date);
              return context.location === "tick"
                ? date
                : `${dayjs(date).format("ddd, MMM DD YYYY")}  (Last Seen: ${
                    target?.points?.ls ? dayjs(target.points.ls * 1000).fromNow() : "few days ago"
                  })`;
            },
          },
        ]}
        series={[
          {
            id: "Economy",
            data: economyData,
            label: "Economy",
            stack: "total",
            color: CabinsColors.economy,
            valueFormatter,
          },
          {
            id: "PremiumEconomy",
            data: premiumEconomyData,
            label: "Premium Economy",
            stack: "total",
            color: CabinsColors.premiumEconomy,
            valueFormatter,
          },
          {
            id: "Business",
            data: businessData,
            label: "Business",
            stack: "total",
            color: CabinsColors.business,
            valueFormatter,
          },
          {
            id: "First",
            data: firstData,
            label: "First",
            stack: "total",
            color: CabinsColors.first,
            valueFormatter,
          },
        ]}
        onItemClick={(event, d) => {
          const date = dates[d.dataIndex];
          props.onItemClick(date);
        }}
        margin={{ top: 50, right: 26, bottom: 40, left: 26 }}
      />
    </div>
  );
}
